import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Download, FileText } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOffers } from "@/hooks/api/use-offers";
import { useAdminOffers } from "@/hooks/api/use-admin";
import { useAuth } from "@/contexts/AuthContext";
import type { Offer } from "@/api/types";
import { cn } from "@/lib/utils";
import apiClient from "@/api/client";

const getDocumentTypeLabel = (type: string, t: (key: string) => string) => {
  switch (type) {
    case 'financial':
      return t('offers.documentTypeFinancial') || 'مالي';
    case 'technical':
      return t('offers.documentTypeTechnical') || 'فني';
    default:
      return '-';
  }
};

const Applications = () => {
  const { t, language, isRTL } = useLanguage();
  const { user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const [activeTab, setActiveTab] = useState("all");
  
  const { data: clientData, isLoading: clientLoading, error: clientError } = useOffers(undefined, { enabled: !isAdmin && !!user });
  const { data: adminData, isLoading: adminLoading, error: adminError } = useAdminOffers(undefined, { enabled: isAdmin && !!user });
  
  const data = isAdmin ? adminData : clientData;
  const isLoading = isAdmin ? adminLoading : clientLoading;
  const error = isAdmin ? adminError : clientError;

  const applications = Array.isArray(data?.data) ? data.data : [];

  const handleDownloadAttachment = async (app: any, attachment: any) => {
    try {
      const response = await apiClient.get(
        `/api/v1/client/offers/${app.id}/attachments/${attachment.id}/download`,
        {
          responseType: 'blob',
        }
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.original_name || attachment.file_name || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{t('applications.pending')}</Badge>;
      case "accepted":
        return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">{t('applications.acceptedBadge')}</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{t('applications.rejectedBadge')}</Badge>;
      default:
        return null;
    }
  };
  const filteredApplications = activeTab === "all" 
    ? applications 
    : applications.filter((app: Offer) => app.status === activeTab);

  const pendingApps = applications.filter((app: Offer) => app.status === "pending");
  const acceptedApps = applications.filter((app: Offer) => app.status === "accepted");
  const rejectedApps = applications.filter((app: Offer) => app.status === "rejected");

  const renderApplications = (apps: Offer[]) => (
    <div className="space-y-3 sm:space-y-4">
      {isLoading ? (
        [1, 2, 3].map((i) => (
          <Card key={i} className="shadow-md">
            <CardContent className="p-4 sm:p-6">
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        ))
      ) : error ? (
        <p className="text-center text-muted-foreground py-8 text-sm sm:text-base">{t('common.error') || 'حدث خطأ أثناء تحميل الطلبات'}</p>
      ) : apps.length === 0 ? (
        <p className="text-center text-muted-foreground py-8 text-sm sm:text-base">{t('applications.noApplications')}</p>
      ) : (
        apps.map((app: Offer) => {
          const tenderTitle = app.tender?.title 
            ? (typeof app.tender.title === 'string' ? app.tender.title : app.tender.title[language] || app.tender.title.en)
            : t('applications.tender.steelSupply');
          
          return (
            <Card key={app.id} className="shadow-md">
              <CardContent className="p-4 sm:p-6">
                <div className={cn(
                  "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4",
                  isRTL ? "sm:flex-row-reverse" : ""
                )}>
                  <div className={cn(
                    "flex-1",
                    isRTL ? "text-right" : "text-left"
                  )}>
                    <h3 className="font-bold text-sm sm:text-base text-foreground">{tenderTitle}</h3>
                    {app.created_at && (
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                        {t('applications.submittedDate')}: {new Date(app.created_at).toLocaleDateString()}
                      </p>
                    )}
                    {app.proposed_amount && (
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {t('applications.submittedPrice')}: {isRTL ? (t('tenders.sar') || 'ر.س') + ' ' : ''}{app.proposed_amount.toLocaleString()}{isRTL ? '' : ' ' + (t('tenders.sar') || 'SAR')}
                      </p>
                    )}
                    {app.attachments && app.attachments.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-muted-foreground mb-1">
                          {t('applications.attachments') || 'المرفقات'}:
                        </p>
                        <div className="space-y-1">
                          {app.attachments.map((attachment: any) => (
                            <div key={attachment.id} className={cn(
                              "flex items-center gap-1 sm:gap-2 text-xs",
                              isRTL ? "flex-row-reverse" : ""
                            )}>
                              <FileText className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                              <span className="text-muted-foreground truncate max-w-[120px] sm:max-w-[150px]" title={attachment.file_name}>
                                {attachment.file_name}
                              </span>
                              <Badge variant="outline" className="text-[9px] sm:text-[10px] px-1 py-0 h-4 flex-shrink-0">
                                {getDocumentTypeLabel(attachment.document_type, t)}
                              </Badge>
                              <span className="text-muted-foreground hidden sm:inline">
                                ({(attachment.file_size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 w-5 p-0 flex-shrink-0"
                                onClick={() => handleDownloadAttachment(app, attachment)}
                                title={t('applications.download') || 'تحميل'}
                              >
                                <Download className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className={cn(
                    "flex sm:block justify-end",
                    isRTL ? "sm:text-left" : "sm:text-right"
                  )}>
                    {getStatusBadge(app.status)}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );

  return (
    <div className="space-y-6 sm:space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('applications.title')}</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1">{t('applications.subtitle')}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4 sm:mb-6 w-full grid grid-cols-4 h-auto gap-1 p-1">
          <TabsTrigger value="all" className="text-xs sm:text-sm px-1 sm:px-3">{t('applications.all')} ({applications.length})</TabsTrigger>
          <TabsTrigger value="pending" className="text-xs sm:text-sm px-1 sm:px-3">{t('applications.pending')} ({pendingApps.length})</TabsTrigger>
          <TabsTrigger value="accepted" className="text-xs sm:text-sm px-1 sm:px-3">{t('applications.accepted')} ({acceptedApps.length})</TabsTrigger>
          <TabsTrigger value="rejected" className="text-xs sm:text-sm px-1 sm:px-3">{t('applications.rejected')} ({rejectedApps.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="all">{renderApplications(filteredApplications)}</TabsContent>
        <TabsContent value="pending">{renderApplications(pendingApps)}</TabsContent>
        <TabsContent value="accepted">{renderApplications(acceptedApps)}</TabsContent>
        <TabsContent value="rejected">{renderApplications(rejectedApps)}</TabsContent>
      </Tabs>
    </div>
  );
};

export default Applications;
